/**
 * Gift With Purchase — controller.
 * Hooks into Liquid Ajax Cart without modifying the cart library.
 */
(function () {
  'use strict';

  var GWP = window.GWP;
  if (!GWP) return;

  /** Prevents evaluate/remove/add loops during GWP-initiated cart mutations. */
  var gwpBusy = false;

  /** Locks all gift Add buttons while a selection is in flight. */
  var gwpSelectionLocked = false;

  /** Debounce rapid cart updates from multiple Liquid Ajax Cart subscribers. */
  var evaluateTimer = null;
  var evaluateRetryCount = 0;
  var MAX_EVALUATE_RETRIES = 20;

  /** Tracks presentment currency for Markets / Global-e currency switches. */
  var gwpLastCurrency = null;

  /** @returns {object | null} */
  function getConfig() {
    return GWP.getConfig();
  }

  /** @returns {{ cart: object | null }} */
  function getCartState() {
    return window.liquidAjaxCart?.getCartState?.() || { cart: null };
  }

  function beginGwpMutation() {
    gwpBusy = true;
  }

  function endGwpMutation() {
    gwpBusy = false;
    scheduleEvaluate(50);
  }

  /**
   * Schedule eligibility evaluation with retry while GWP cart mutations are in flight.
   *
   * @param {number} [delay]
   */
  function scheduleEvaluate(delay) {
    clearTimeout(evaluateTimer);
    evaluateTimer = setTimeout(function () {
      if (gwpBusy) {
        if (evaluateRetryCount < MAX_EVALUATE_RETRIES) {
          evaluateRetryCount++;
          scheduleEvaluate(100);
        }
        return;
      }

      evaluateRetryCount = 0;
      evaluateGiftEligibility();
    }, delay || 75);
  }

  /**
   * @param {string} message
   */
  function showDrawerError(message) {
    var errorEl = document.querySelector('[data-gwp-drawer-error]');
    if (!errorEl) return;

    if (!message) {
      errorEl.hidden = true;
      errorEl.textContent = '';
      return;
    }

    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getDrawer() {
    return document.querySelector('[data-gwp-drawer]');
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  function getActiveGwpSwatch(card) {
    return card.querySelector(
      '[data-gwp-product-swatch][aria-pressed="true"]:not([hidden])',
    );
  }

  /**
   * @param {HTMLElement} card
   * @returns {HTMLElement | null}
   */
  function getFirstVisibleGwpSwatch(card) {
    return card.querySelector('[data-gwp-product-swatch]:not([hidden])');
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} swatch
   */
  function selectGwpSwatch(card, swatch) {
    if (!card || !swatch || swatch.hidden) return;

    card.querySelectorAll('[data-gwp-product-swatch]').forEach(function (el) {
      el.setAttribute('aria-pressed', el === swatch ? 'true' : 'false');
    });

    var imageEl = card.querySelector('[data-gwp-gift-image]');
    var imageUrl = swatch.getAttribute('data-image-url');

    if (imageEl && imageUrl) {
      if (imageEl.tagName === 'IMG') {
        imageEl.src = imageUrl;
        imageEl.removeAttribute('srcset');
        imageEl.removeAttribute('sizes');
      }
    }

    var titleNameEl = card.querySelector('[data-gwp-gift-title-name]');
    var titleColorEl = card.querySelector('[data-gwp-gift-title-color]');
    var productTitle = swatch.getAttribute('data-product-title') || '';
    var productColor = swatch.getAttribute('data-product-color') || '';

    if (titleNameEl) titleNameEl.textContent = productTitle;

    if (titleColorEl) {
      titleColorEl.textContent = productColor ? '(' + productColor + ')' : '';
      titleColorEl.hidden = !productColor;
    }

    var compareEl = card.querySelector('[data-gwp-gift-price-compare]');
    var price = swatch.getAttribute('data-price') || '';

    if (compareEl) {
      compareEl.textContent = price;
      compareEl.hidden = !price;
    }

    var variantId = swatch.getAttribute('data-variant-id') || '';
    var productId = swatch.getAttribute('data-product-id') || '';
    var select = card.querySelector('[data-gwp-variant-select]');
    var button = card.querySelector('[data-gwp-select-gift]');

    if (select) select.value = variantId;
    if (productId) card.setAttribute('data-product-id', productId);
    if (button) {
      if (productId) button.setAttribute('data-product-id', productId);
      button.setAttribute('data-default-variant-id', variantId);
    }

    syncGiftCardState(card);
  }

  /**
   * @param {HTMLElement} card
   * @param {{ items?: object[] } | null} [cart]
   */
  function filterCollectionSwatchesByCart(card, cart) {
    var swatches = card.querySelectorAll('[data-gwp-product-swatch]');
    var hasVisibleSwatch = false;

    swatches.forEach(function (swatch) {
      var productId = swatch.getAttribute('data-product-id');
      var excluded =
        productId && cart && GWP.isProductInCartAsNonGift(cart, productId);

      swatch.hidden = Boolean(excluded);
      if (!excluded) hasVisibleSwatch = true;
    });

    card.hidden = !hasVisibleSwatch;

    if (!hasVisibleSwatch) return;

    var activeSwatch = getActiveGwpSwatch(card);
    if (!activeSwatch) {
      var firstSwatch = getFirstVisibleGwpSwatch(card);
      if (firstSwatch) selectGwpSwatch(card, firstSwatch);
    }
  }

  /**
   * @param {{ items?: object[] } | null} [cart]
   */
  function filterGiftCardsByCart(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    cart = cart || getCartState().cart;

    var collectionCard = drawer.querySelector('[data-gwp-gift-card-collection]');
    if (collectionCard) {
      filterCollectionSwatchesByCart(collectionCard, cart);
      return;
    }

    drawer.querySelectorAll('[data-gwp-gift-card]').forEach(function (card) {
      var productId = card.getAttribute('data-product-id');
      if (!productId) return;

      card.hidden = Boolean(
        cart && GWP.isProductInCartAsNonGift(cart, productId),
      );
    });
  }

  function hasVisibleGiftCards() {
    var drawer = getDrawer();
    if (!drawer) return false;

    var collectionCard = drawer.querySelector('[data-gwp-gift-card-collection]');
    if (collectionCard) {
      if (collectionCard.hidden) return false;

      return Array.from(
        collectionCard.querySelectorAll('[data-gwp-product-swatch]'),
      ).some(function (swatch) {
        return !swatch.hidden;
      });
    }

    return Array.from(drawer.querySelectorAll('[data-gwp-gift-card]')).some(
      function (card) {
        return !card.hidden;
      },
    );
  }

  /**
   * @param {{ items?: object[] } | null} [cart]
   */
  function showGiftDrawer(cart) {
    var config = getConfig();
    var drawer = getDrawer();

    if (!config?.enabled || !drawer || !config.gifts?.length) return;

    cart = cart || getCartState().cart;
    filterGiftCardsByCart(cart);

    if (!hasVisibleGiftCards()) {
      hideGiftDrawer();
      return;
    }

    showDrawerError('');
    drawer.hidden = false;
    syncAllGiftCards();
  }

  function hideGiftDrawer() {
    var drawer = getDrawer();
    if (drawer) drawer.hidden = true;
  }

  /**
   * Refresh cart section after GWP cart mutations.
   *
   * @param {() => void} [callback]
   */
  function refreshCartSections(callback) {
    if (!window.liquidAjaxCart?.cartRequestUpdate) {
      if (callback) callback();
      return;
    }

    window.liquidAjaxCart.cartRequestUpdate(
      { sections: 'main-cart' },
      {
        lastComplete: function () {
          if (callback) callback();
        },
      },
    );
  }

  /** @param {object} lineItem @returns {Promise<void>} */
  function removeLineItem(lineItem) {
    return new Promise(function (resolve) {
      if (!window.liquidAjaxCart?.cartRequestChange) {
        resolve();
        return;
      }

      window.liquidAjaxCart.cartRequestChange(
        {
          id: lineItem.key,
          quantity: 0,
        },
        {
          lastComplete: function () {
            resolve();
          },
        },
      );
    });
  }

  /** @param {object} cart @returns {Promise<void>} */
  function removeGiftFromCart(cart) {
    var giftItems = GWP.getGiftLineItems(cart);
    if (!giftItems.length) return Promise.resolve();

    beginGwpMutation();

    var chain = Promise.resolve();

    giftItems.forEach(function (item) {
      chain = chain.then(function () {
        return removeLineItem(item);
      });
    });

    return chain
      .then(function () {
        return new Promise(function (resolve) {
          refreshCartSections(resolve);
        });
      })
      .finally(endGwpMutation);
  }

  /**
   * Cap a single GWP line to quantity 1 (one gift per order).
   * @returns {Promise<boolean>}
   */
  function normalizeGiftQuantity(cart) {
    var gift = GWP.getGiftLineItem(cart);
    if (!gift || gift.quantity <= 1) return Promise.resolve(false);

    beginGwpMutation();

    return new Promise(function (resolve) {
      window.liquidAjaxCart.cartRequestChange(
        {
          id: gift.key,
          quantity: 1,
        },
        {
          lastComplete: function () {
            refreshCartSections(function () {
              endGwpMutation();
              resolve(true);
            });
          },
        },
      );
    });
  }

  /**
   * Remove duplicate or invalid GWP lines, keeping the first valid gift.
   * @returns {Promise<boolean>}
   */
  function dedupeGiftLines(cart, config) {
    var giftItems = GWP.getGiftLineItems(cart);
    if (!giftItems.length) return Promise.resolve(false);

    var validGifts = giftItems.filter(function (item) {
      return GWP.isValidGiftVariant(item.variant_id, config);
    });

    var invalidGifts = giftItems.filter(function (item) {
      return !GWP.isValidGiftVariant(item.variant_id, config);
    });

    var extras = [];

    if (validGifts.length > 1) {
      extras = extras.concat(validGifts.slice(1));
    }

    extras = extras.concat(invalidGifts);

    if (!extras.length) return Promise.resolve(false);

    beginGwpMutation();

    var chain = Promise.resolve();

    extras.forEach(function (item) {
      chain = chain.then(function () {
        return removeLineItem(item);
      });
    });

    return chain
      .then(function () {
        return new Promise(function (resolve) {
          refreshCartSections(function () {
            resolve(true);
          });
        });
      })
      .finally(endGwpMutation);
  }

  /**
   * @param {number | string} variantId
   * @returns {Promise<boolean>}
   */
  function addGiftToCart(variantId) {
    var config = getConfig();
    var cart = getCartState().cart;

    if (!config?.enabled) return Promise.resolve(false);
    if (!GWP.isValidGiftVariant(variantId, config)) return Promise.resolve(false);
    if (!GWP.isGiftVariantAvailable(variantId, config)) {
      showDrawerError('This gift is currently out of stock. Please choose another option.');
      return Promise.resolve(false);
    }
    if (cart && GWP.getGiftQuantity(cart) >= 1) {
      showDrawerError('A free gift is already in your bag.');
      return Promise.resolve(false);
    }

    var giftProductId = GWP.getGiftProductIdForVariant(variantId, config);
    if (giftProductId && cart && GWP.isProductInCartAsNonGift(cart, giftProductId)) {
      showDrawerError('This item is already in your bag and cannot be added as a free gift.');
      return Promise.resolve(false);
    }

    if (!cart || !GWP.isEligibleForGift(cart, config)) {
      showDrawerError(
        'Your bag no longer qualifies for a free gift. Add more full-priced items to reach the offer threshold.',
      );
      return Promise.resolve(false);
    }
    if (!window.liquidAjaxCart?.cartRequestAdd) return Promise.resolve(false);

    beginGwpMutation();

    return new Promise(function (resolve) {
      window.liquidAjaxCart.cartRequestAdd(
        {
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _gwp: 'true',
                _gwp_offer: config.offerId,
              },
            },
          ],
        },
        {
          lastComplete: function (requestState) {
            var ok = requestState?.responseData?.ok;

            if (!ok) {
              endGwpMutation();
              showDrawerError('Unable to add this gift. Please try again or choose another option.');
              resolve(false);
              return;
            }

            hideGiftDrawer();
            refreshCartSections(function () {
              endGwpMutation();
              resolve(true);
            });
          },
        },
      );
    });
  }

  function applyEligibilityDecision(cart, config) {
    var eligible = GWP.isEligibleForGift(cart, config);
    var hasGift = GWP.hasGiftInCart(cart);

    if (!eligible) {
      hideGiftDrawer();
      if (hasGift) removeGiftFromCart(cart);
      return;
    }

    if (hasGift) {
      hideGiftDrawer();
      return;
    }

    showGiftDrawer(cart);
  }

  /** Main eligibility orchestrator — called after every successful cart change. */
  function evaluateGiftEligibility(cart) {
    if (gwpBusy) {
      scheduleEvaluate(100);
      return;
    }

    var config = getConfig();
    if (!config?.enabled) return;

    cart = cart || getCartState().cart;
    if (!cart) return;

    normalizeGiftQuantity(cart)
      .then(function () {
        cart = getCartState().cart || cart;
        return dedupeGiftLines(cart, config);
      })
      .then(function () {
        cart = getCartState().cart || cart;

        if (!cart && window.liquidAjaxCart?.cartRequestGet) {
          window.liquidAjaxCart.cartRequestGet({
            lastComplete: function () {
              cart = getCartState().cart;
              if (!cart) return;

              GWP.ensureProductTagData(cart, config).then(function () {
                applyEligibilityDecision(getCartState().cart || cart, config);
              });
            },
          });
          return;
        }

        return GWP.ensureProductTagData(cart, config).then(function () {
          applyEligibilityDecision(getCartState().cart || cart, config);
        });
      });
  }

  /**
   * @returns {boolean}
   */
  function isGiftSelectionBlocked() {
    if (gwpBusy || gwpSelectionLocked) return true;

    var cart = getCartState().cart;
    return Boolean(cart && GWP.getGiftQuantity(cart) >= 1);
  }

  function lockGiftSelection() {
    gwpSelectionLocked = true;

    var drawer = getDrawer();
    if (drawer) drawer.setAttribute('data-gwp-selection-locked', '');

    syncAllGiftCards();
  }

  function unlockGiftSelection() {
    gwpSelectionLocked = false;

    var drawer = getDrawer();
    if (drawer) drawer.removeAttribute('data-gwp-selection-locked');

    syncAllGiftCards();
  }

  /**
   * Sync variant select state for a single gift card.
   *
   * @param {HTMLElement} card
   */
  function syncGiftCardState(card) {
    var select = card.querySelector('[data-gwp-variant-select]');
    var button = card.querySelector('[data-gwp-select-gift]');
    var status = card.querySelector('[data-gwp-gift-status]');
    var isCollectionCard = card.hasAttribute('data-gwp-gift-card-collection');

    if (!button || card.hidden) return;

    var variantId =
      select?.value || button.getAttribute('data-default-variant-id') || '';

    var available = GWP.isGiftVariantAvailable(variantId);
    var blocked = isGiftSelectionBlocked();
    var productId = card.getAttribute('data-product-id');
    var cart = getCartState().cart;
    var inCartAsPaid =
      productId && cart && GWP.isProductInCartAsNonGift(cart, productId);

    button.disabled = blocked || !available || inCartAsPaid;
    card.setAttribute('aria-disabled', blocked ? 'true' : 'false');

    if (isCollectionCard) {
      card.querySelectorAll('[data-gwp-product-swatch]').forEach(function (swatch) {
        var swatchAvailable = swatch.getAttribute('data-available') !== 'false';
        swatch.disabled = blocked || swatch.hidden || !swatchAvailable;
      });
    }

    if (status) {
      if (!blocked && !available) {
        status.hidden = false;
        status.textContent = 'This option is out of stock.';
      } else {
        status.hidden = true;
        status.textContent = '';
      }
    }
  }

  /**
   * @param {ParentNode} [root]
   */
  function syncAllGiftCards(root) {
    filterGiftCardsByCart(getCartState().cart);
    root = root || document;
    root.querySelectorAll('[data-gwp-gift-card]').forEach(syncGiftCardState);
  }

  function initGiftCards(root) {
    syncAllGiftCards(root);
  }

  /**
   * @param {MouseEvent} event
   */
  function onGiftSelectClick(event) {
    var button = event.target.closest('[data-gwp-select-gift]');
    if (!button || button.disabled || isGiftSelectionBlocked()) return;

    var card = button.closest('[data-gwp-gift-card]');
    if (!card || card.hidden) return;

    var select = card.querySelector('[data-gwp-variant-select]');
    var variantId =
      select?.value || button.getAttribute('data-default-variant-id');

    if (!variantId) {
      showDrawerError('Please select a gift option.');
      return;
    }

    lockGiftSelection();

    addGiftToCart(variantId).finally(function () {
      unlockGiftSelection();
      syncAllGiftCards();
    });
  }

  /**
   * @param {Event} event
   */
  function onVariantChange(event) {
    var select = event.target.closest('[data-gwp-variant-select]');
    if (!select) return;

    var card = select.closest('[data-gwp-gift-card]');
    if (card) syncAllGiftCards();
  }

  /**
   * @param {MouseEvent} event
   */
  function onGwpSwatchClick(event) {
    var swatch = event.target.closest('[data-gwp-product-swatch]');
    if (!swatch || swatch.hidden || swatch.disabled || isGiftSelectionBlocked()) {
      return;
    }

    var card = swatch.closest('[data-gwp-gift-card-collection]');
    if (!card || card.hidden) return;

    selectGwpSwatch(card, swatch);
  }

  function trackCurrencyChange() {
    var active = window.Shopify?.currency?.active;
    if (!active) return;

    if (gwpLastCurrency && active !== gwpLastCurrency) {
      scheduleEvaluate();
    }

    gwpLastCurrency = active;
  }

  function onCartReady() {
    trackCurrencyChange();
    initGiftCards(document);
    evaluateGiftEligibility();
  }

  function onCartUpdated() {
    scheduleEvaluate();
  }

  function bindGwpEvents() {
    document.addEventListener('click', onGiftSelectClick);
    document.addEventListener('click', onGwpSwatchClick);
    document.addEventListener('change', onVariantChange);

    document.addEventListener('liquidAjaxCartInit', onCartReady);
    document.addEventListener('DOMContentLoaded', onCartReady);

    if (window.liquidAjaxCart?.subscribeToCartAjaxRequests) {
      window.liquidAjaxCart.subscribeToCartAjaxRequests(function (requestState, subscribeToResult) {
        subscribeToResult(function (state) {
          if (!state.responseData?.ok) return;
          onCartUpdated();
        });
      });
    }

    if (window.liquidAjaxCart?.subscribeToCartSectionsUpdate) {
      window.liquidAjaxCart.subscribeToCartSectionsUpdate(function () {
        trackCurrencyChange();
        initGiftCards(document);
        scheduleEvaluate();
      });
    }

    if (window.liquidAjaxCart?.subscribeToCartStateUpdate) {
      window.liquidAjaxCart.subscribeToCartStateUpdate(function (state) {
        if (!state.status?.cartStateSet) return;
        trackCurrencyChange();
        scheduleEvaluate();
      });
    }

    window.addEventListener('pageshow', function () {
      trackCurrencyChange();
      scheduleEvaluate();
    });
  }

  bindGwpEvents();

  if (window.liquidAjaxCart?.getCartState?.()?.status?.cartStateSet) {
    onCartReady();
  }

  window.GWPController = {
    evaluateGiftEligibility: evaluateGiftEligibility,
    addGiftToCart: addGiftToCart,
    removeGiftFromCart: removeGiftFromCart,
  };
})();
